
import { supabase } from '@/integrations/supabase/client';

export interface SemanticSearchResult {
  chunkId: string;
  documentId: string;
  title: string;
  content: string;
  category: string;
  similarity: number;
}

export interface SemanticSearchOptions {
  query: string;
  categories?: string[];
  matchThreshold?: number;
  maxResults?: number;
}

export const searchDocumentsSemanticly = async (options: SemanticSearchOptions): Promise<{
  results: SemanticSearchResult[];
  searchType: 'semantic' | 'text_fallback';
  success: boolean;
}> => {
  try {
    console.log('🔍 [SEMANTIC-SERVICE] Performing semantic search:', options.query);
    console.log('🔍 [SEMANTIC-SERVICE] Using threshold:', options.matchThreshold || 0.3);

    const { data, error } = await supabase.functions.invoke('semantic-search', {
      body: {
        query: options.query,
        categories: options.categories,
        matchThreshold: options.matchThreshold || 0.3, // LOWERED from 0.5 to 0.3 for better recall
        maxResults: options.maxResults || 10
      }
    });

    if (error) {
      console.error('🔍 [SEMANTIC-SERVICE] Semantic search error:', error);
      
      // Enhanced fallback to direct database search
      console.log('🔍 [SEMANTIC-SERVICE] Attempting direct database fallback...');
      return await performDirectDatabaseSearch(options);
    }

    console.log('🔍 [SEMANTIC-SERVICE] Semantic search response:', {
      success: data.success,
      resultsCount: data.results?.length || 0,
      searchType: data.searchType
    });

    if (data.results && data.results.length > 0) {
      console.log('🔍 [SEMANTIC-SERVICE] Found results:', data.results.map((r: any) => ({
        title: r.title,
        similarity: r.similarity,
        category: r.category
      })));
    } else {
      console.log('🔍 [SEMANTIC-SERVICE] No semantic results, trying direct database fallback...');
      return await performDirectDatabaseSearch(options);
    }

    return {
      results: data.results || [],
      searchType: data.searchType || 'semantic',
      success: data.success || false
    };

  } catch (error) {
    console.error('🔍 [SEMANTIC-SERVICE] Semantic search service error:', error);
    
    // Enhanced error handling with direct database fallback
    console.log('🔍 [SEMANTIC-SERVICE] Service error, attempting direct database fallback...');
    return await performDirectDatabaseSearch(options);
  }
};

/**
 * Enhanced direct database search as fallback
 */
async function performDirectDatabaseSearch(options: SemanticSearchOptions): Promise<{
  results: SemanticSearchResult[];
  searchType: 'semantic' | 'text_fallback';
  success: boolean;
}> {
  try {
    console.log('🔍 [SEMANTIC-SERVICE] Performing direct database search...');
    
    // Enhanced search terms for NAV documents
    const searchTerms = generateEnhancedSearchTerms(options.query);
    console.log('🔍 [SEMANTIC-SERVICE] Enhanced search terms:', searchTerms);
    
    for (const searchTerm of searchTerms) {
      let queryBuilder = supabase
        .from('knowledge_documents')
        .select(`
          id,
          title,
          content,
          category,
          created_at,
          chunk_count
        `)
        .eq('is_active', true);

      // Add category filter if specified
      if (options.categories && options.categories.length > 0) {
        queryBuilder = queryBuilder.in('category', options.categories);
      }

      // Try multiple search strategies
      const searchStrategies = [
        // Strategy 1: Full text search on content
        () => queryBuilder.textSearch('content', searchTerm),
        // Strategy 2: Title and content ilike search
        () => queryBuilder.or(`title.ilike.%${searchTerm}%,content.ilike.%${searchTerm}%`),
        // Strategy 3: Content-only ilike search
        () => queryBuilder.ilike('content', `%${searchTerm}%`)
      ];

      for (const [index, strategy] of searchStrategies.entries()) {
        try {
          console.log(`🔍 [SEMANTIC-SERVICE] Trying search strategy ${index + 1} with term: "${searchTerm}"`);
          
          const { data, error } = await strategy().limit(options.maxResults || 10);
          
          if (!error && data && data.length > 0) {
            console.log(`🔍 [SEMANTIC-SERVICE] Strategy ${index + 1} successful: found ${data.length} documents`);
            
            // Transform to semantic search result format
            const results: SemanticSearchResult[] = data.map(doc => ({
              chunkId: doc.id,
              documentId: doc.id,
              title: doc.title,
              content: doc.content, // Use full content
              category: doc.category,
              similarity: 0.8 - (index * 0.1) // Slightly lower similarity for each strategy
            }));

            // Log what we found for debugging
            console.log('🔍 [SEMANTIC-SERVICE] Documents found:', data.map(d => ({
              title: d.title,
              category: d.category,
              contentLength: d.content?.length || 0,
              chunkCount: d.chunk_count
            })));

            return {
              results,
              searchType: 'text_fallback',
              success: true
            };
          }
          
          if (error) {
            console.warn(`🔍 [SEMANTIC-SERVICE] Strategy ${index + 1} error:`, error);
          }
        } catch (strategyError) {
          console.error(`🔍 [SEMANTIC-SERVICE] Strategy ${index + 1} failed:`, strategyError);
        }
      }
    }

    console.log('🔍 [SEMANTIC-SERVICE] All search strategies failed');
    return {
      results: [],
      searchType: 'text_fallback',
      success: false
    };

  } catch (error) {
    console.error('🔍 [SEMANTIC-SERVICE] Direct database search failed:', error);
    return {
      results: [],
      searchType: 'text_fallback',
      success: false
    };
  }
}

/**
 * Generate enhanced search terms for better document matching
 */
function generateEnhancedSearchTerms(query: string): string[] {
  const lowerQuery = query.toLowerCase();
  const terms: string[] = [];
  
  // Always include original query
  terms.push(query);
  
  // Enhanced NAV-specific terms
  if (lowerQuery.includes('nav') || lowerQuery.includes('rapport')) {
    terms.push(
      'omverdenanalyse',
      'arbeidsmarkedsanalyse',
      'arbeidskraft',
      'etterspørsel',
      'kompetanse',
      'fremtid',
      'prognose',
      'bransjer',
      'sektor',
      'næring'
    );
  }
  
  // Future/development terms
  if (lowerQuery.includes('fremtid') || lowerQuery.includes('fremover')) {
    terms.push(
      'prognose',
      'fremtidsutsikter',
      'utvikling',
      'trend',
      'perspektiv',
      'behovsprognose'
    );
  }
  
  // Industry/sector terms
  if (lowerQuery.includes('bransje') || lowerQuery.includes('arbeidskraft')) {
    terms.push(
      'helse',
      'teknologi',
      'bygg',
      'utdanning',
      'omsorg',
      'it',
      'digital',
      'grønn',
      'miljø'
    );
  }
  
  // Remove duplicates and return
  return [...new Set(terms)];
}

export const formatSemanticSearchResults = (results: SemanticSearchResult[]): string => {
  if (results.length === 0) {
    return 'Ingen relevante dokumenter funnet.';
  }

  let formattedResults = `## Relevante dokumenter funnet (${results.length}):\n\n`;
  
  results.forEach((result, index) => {
    formattedResults += `### ${index + 1}. ${result.title}\n`;
    formattedResults += `**Kategori:** ${result.category}\n`;
    formattedResults += `**Relevans:** ${(result.similarity * 100).toFixed(1)}%\n\n`;
    
    // Enhanced content formatting - show more content for better context
    const contentPreview = result.content.length > 2000 
      ? result.content.substring(0, 2000) + '...' 
      : result.content;
    
    formattedResults += `${contentPreview}\n\n`;
    formattedResults += `---\n\n`;
  });

  return formattedResults;
};
